
'use client';

import { useState, useEffect } from 'react';
import { GradientText } from '@/components/ui/gradient-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';

interface JobOpening {
  id: number;
  title: string;
  location: string;
  description: string;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

async function fetchJobOpenings(): Promise<{
  success: boolean;
  data?: JobOpening[];
  message?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/careers`);
    if (!response.ok) {
        throw new Error('Failed to fetch job openings');
    }
    const result = await response.json();
    return { success: result.success, data: result.data, message: result.message };
  } catch (error) {
    console.error("Error fetching job openings:", error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
    return { success: false, message: errorMessage };
  }
}

export default function CareersPage() {
  const [jobOpenings, setJobOpenings] = useState<JobOpening[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadJobs() {
      setIsLoading(true);
      const { success, data, message } = await fetchJobOpenings();
      if (success && data) {
        setJobOpenings(data);
      } else {
        setError(message || 'Could not load job openings.');
      }
      setIsLoading(false);
    }
    loadJobs();
  }, []);

  return (
    <div className="container mx-auto max-w-5xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Join Our <GradientText>Team</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          We are always looking for talented and passionate individuals to join us on our mission.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-destructive-foreground bg-destructive/80 p-4 rounded-md">
            <p className="font-bold">Failed to load job openings.</p>
            <p className="text-sm">{error}</p>
        </div>
      ) : jobOpenings.length > 0 ? (
        <div className="grid gap-8">
          {jobOpenings.map((job) => (
            <Card key={job.id}>
              <CardHeader>
                <CardTitle>{job.title}</CardTitle>
                <CardDescription>{job.location}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{job.description}</p>
              </CardContent>
              <CardFooter>
                <Button asChild>
                  <Link href={`/apply/${job.id}`}>Apply Now</Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
         <div className="text-center text-muted-foreground py-16">
          <p className="text-lg">There are no open positions at this time. Please check back later!</p>
        </div>
      )}
    </div>
  );
}
